<template>
  <div class="provably-fair">
    <h2 class="page-title">{{ $t('player.provably_fair.title') }}</h2>
    <p class="page-desc">{{ $t('player.provably_fair.desc') }}</p>

    <!-- Active Seeds Section -->
    <div class="section">
      <h3 class="section-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        {{ $t('player.provably_fair.active_seed_title') }}
      </h3>

      <!-- Loading -->
      <div v-if="store.loadingActive" class="seeds-card">
        <div class="seed-row-skeleton" v-for="i in 3" :key="i">
          <div class="skel-label"></div>
          <div class="skel-value"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="store.activeError" class="pf-error">
        <p>{{ store.activeError }}</p>
        <button class="btn-cta" @click="store.fetchActiveSeeds()">{{ $t('common.try_again') }}</button>
      </div>

      <!-- Seed Info -->
      <div v-else-if="store.activeSeeds" class="seeds-card">
        <div class="seed-row">
          <span class="seed-label">{{ $t('player.provably_fair.server_seed_hash') }}</span>
          <div class="seed-value-wrap">
            <code class="seed-value hash">{{ store.activeSeeds.server_seed_hash }}</code>
            <button class="btn-copy" :title="$t('player.provably_fair.copy_hash')" @click="copyToClipboard(store.activeSeeds!.server_seed_hash)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
          <span class="seed-hint">{{ $t('player.provably_fair.server_seed_hash_hint') }}</span>
        </div>

        <div class="seed-row">
          <span class="seed-label">{{ $t('player.provably_fair.client_seed') }}</span>
          <div class="seed-value-wrap">
            <code class="seed-value">{{ store.activeSeeds.client_seed }}</code>
            <button class="btn-copy" :title="$t('player.provably_fair.copy_client_seed')" @click="copyToClipboard(store.activeSeeds!.client_seed)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
          <span class="seed-hint">{{ $t('player.provably_fair.client_seed_hint') }}</span>
        </div>

        <div class="seed-row">
          <span class="seed-label">{{ $t('player.provably_fair.nonce') }}</span>
          <code class="seed-value nonce">{{ store.activeSeeds.nonce }}</code>
          <span class="seed-hint">{{ $t('player.provably_fair.nonce_hint') }}</span>
        </div>
      </div>
    </div>

    <!-- Update Client Seed Section -->
    <div class="section">
      <h3 class="section-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        {{ $t('player.provably_fair.set_client_seed_title') }}
      </h3>

      <div class="client-seed-form">
        <div class="input-group">
          <input
            v-model="newClientSeed"
            type="text"
            class="text-input"
            :placeholder="$t('player.provably_fair.client_seed_placeholder')"
            maxlength="256"
            :disabled="store.updatingClientSeed"
          >
          <span class="char-count" :class="{ warn: newClientSeed.length > 240 }">
            {{ newClientSeed.length }}/256
          </span>
        </div>
        <div v-if="clientSeedValidation" class="validation-msg">{{ clientSeedValidation }}</div>
        <div v-if="store.clientSeedError" class="validation-msg error">{{ store.clientSeedError }}</div>
        <div v-if="clientSeedSuccess" class="validation-msg success">{{ $t('player.provably_fair.client_seed_success') }}</div>
        <button
          class="btn-primary"
          :disabled="!canUpdateClientSeed"
          @click="handleUpdateClientSeed"
        >
          <svg v-if="store.updatingClientSeed" class="spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          {{ store.updatingClientSeed ? $t('player.provably_fair.updating') : $t('player.provably_fair.update_client_seed') }}
        </button>
      </div>
    </div>

    <!-- Rotate Seed Section -->
    <div class="section">
      <h3 class="section-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        {{ $t('player.provably_fair.rotate_seed_title') }}
      </h3>
      <p class="section-desc">{{ $t('player.provably_fair.rotate_desc') }}</p>

      <!-- Confirm Modal -->
      <div v-if="showRotateConfirm" class="confirm-modal-backdrop" @click.self="showRotateConfirm = false">
        <div class="confirm-modal">
          <h4 class="modal-title">{{ $t('player.provably_fair.rotate_modal_title') }}</h4>
          <p class="modal-desc">{{ $t('player.provably_fair.rotate_modal_desc') }}</p>
          <div class="modal-actions">
            <button class="btn-secondary" @click="showRotateConfirm = false">{{ $t('common.cancel') }}</button>
            <button
              class="btn-danger"
              :disabled="store.rotating"
              @click="handleRotate"
            >
              <svg v-if="store.rotating" class="spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
              {{ store.rotating ? $t('player.provably_fair.rotating') : $t('player.provably_fair.rotate_seed') }}
            </button>
          </div>
          <div v-if="store.rotationError" class="validation-msg error">{{ store.rotationError }}</div>
        </div>
      </div>

      <button class="btn-rotate" @click="showRotateConfirm = true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        {{ $t('player.provably_fair.rotate_seed_pair') }}
      </button>

      <!-- Rotation Result -->
      <div v-if="store.lastRotation" class="rotation-result">
        <h4 class="rotation-title">{{ $t('player.provably_fair.rotation_success') }}</h4>
        <div class="rotation-compare">
          <div class="rotation-col old">
            <span class="rotation-label">{{ $t('player.provably_fair.rotation_old_label') }}</span>
            <div class="rotation-field">
              <span class="rotation-field-label">{{ $t('player.provably_fair.server_seed_field') }}</span>
              <div class="seed-value-wrap">
                <code class="seed-value hash">{{ store.lastRotation.old_server_seed }}</code>
                <button class="btn-copy" :title="$t('player.provably_fair.copy_old_seed')" @click="copyToClipboard(store.lastRotation!.old_server_seed)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="rotation-field">
              <span class="rotation-field-label">{{ $t('player.provably_fair.hash_field') }}</span>
              <code class="seed-value hash">{{ store.lastRotation.old_server_seed_hash }}</code>
            </div>
          </div>
          <div class="rotation-divider">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
          <div class="rotation-col new">
            <span class="rotation-label">{{ $t('player.provably_fair.rotation_new_label') }}</span>
            <div class="rotation-field">
              <span class="rotation-field-label">{{ $t('player.provably_fair.server_seed_hash') }}</span>
              <code class="seed-value hash">{{ store.lastRotation.new_seed_hash }}</code>
            </div>
            <div class="rotation-field">
              <span class="rotation-field-label">{{ $t('player.provably_fair.client_seed') }}</span>
              <code class="seed-value">{{ store.lastRotation.new_client_seed }}</code>
            </div>
          </div>
        </div>
        <p class="rotation-verify-hint">{{ $t('player.provably_fair.rotation_verify_hint') }}</p>
        <button class="btn-text" @click="store.clearRotation()">{{ $t('player.provably_fair.dismiss') }}</button>
      </div>
    </div>

    <!-- Seed History Section -->
    <div class="section">
      <h3 class="section-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        {{ $t('player.provably_fair.history_title') }}
      </h3>

      <!-- Loading -->
      <div v-if="store.loadingHistory" class="history-loading">
        <div v-for="i in 3" :key="i" class="history-row-skeleton">
          <div class="skel-label"></div>
          <div class="skel-value"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="store.historyError" class="pf-error">
        <p>{{ store.historyError }}</p>
        <button class="btn-cta" @click="store.fetchHistory()">{{ $t('common.try_again') }}</button>
      </div>

      <!-- Empty -->
      <div v-else-if="store.history.length === 0" class="pf-empty">
        <p>{{ $t('player.provably_fair.no_history') }}</p>
        <p class="pf-empty-sub">{{ $t('player.provably_fair.no_history_hint') }}</p>
      </div>

      <!-- History Table -->
      <div v-else>
        <div class="history-table-wrap">
          <div class="history-header">
            <span>{{ $t('player.provably_fair.col_date') }}</span>
            <span>{{ $t('player.provably_fair.col_server_seed') }}</span>
            <span>{{ $t('player.provably_fair.col_hash') }}</span>
            <span>{{ $t('player.provably_fair.col_client_seed') }}</span>
            <span>{{ $t('player.provably_fair.col_nonce') }}</span>
          </div>
          <div
            v-for="entry in store.history"
            :key="entry.id"
            class="history-row"
          >
            <span class="history-date">{{ formatDate(entry.revealed_at || entry.created_at) }}</span>
            <span class="history-seed">
              <code class="mono-sm">{{ truncate(entry.server_seed, 12) }}</code>
              <button class="btn-copy-sm" :title="$t('player.provably_fair.copy_server_seed')" @click="copyToClipboard(entry.server_seed)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </button>
            </span>
            <span class="history-hash">
              <code class="mono-sm">{{ truncate(entry.server_seed_hash, 12) }}</code>
            </span>
            <span class="history-client">
              <code class="mono-sm">{{ truncate(entry.client_seed, 10) }}</code>
            </span>
            <span class="history-nonce">{{ entry.nonce }}</span>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="store.historyMeta.last_page > 1" class="history-pagination">
          <button
            class="btn-page"
            :disabled="store.historyMeta.current_page <= 1"
            @click="store.fetchHistory(store.historyMeta.current_page - 1)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>
          <span class="page-info">
            {{ $t('common.page_of', { current: store.historyMeta.current_page, total: store.historyMeta.last_page }) }}
          </span>
          <button
            class="btn-page"
            :disabled="store.historyMeta.current_page >= store.historyMeta.last_page"
            @click="store.fetchHistory(store.historyMeta.current_page + 1)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Link to Public Verification -->
    <div class="section verify-link-section">
      <router-link :to="{ name: 'provably-fair.verify' }" class="btn-verify-link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        {{ $t('player.provably_fair.open_verification') }}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
        </svg>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProvablyFairStore } from '@/stores/provablyFair'

const { t } = useI18n()
const store = useProvablyFairStore()

const newClientSeed = ref('')
const clientSeedSuccess = ref(false)
const showRotateConfirm = ref(false)
const copyFeedback = ref<string | null>(null)

const clientSeedValidation = computed(() => {
  if (newClientSeed.value.length === 0) return null
  if (newClientSeed.value.length > 256) return t('player.provably_fair.validation_max_length')
  if (!/^[\w\s!@#$%^&*()\-+=\[\]{};:'",.<>?/\\|`~]+$/.test(newClientSeed.value)) {
    return t('player.provably_fair.validation_invalid_chars')
  }
  return null
})

const canUpdateClientSeed = computed(() => {
  return (
    newClientSeed.value.length > 0 &&
    newClientSeed.value.length <= 256 &&
    !clientSeedValidation.value &&
    !store.updatingClientSeed
  )
})

async function handleUpdateClientSeed() {
  if (!canUpdateClientSeed.value) return
  clientSeedSuccess.value = false

  const success = await store.updateClientSeed(newClientSeed.value)
  if (success) {
    clientSeedSuccess.value = true
    newClientSeed.value = ''
    setTimeout(() => {
      clientSeedSuccess.value = false
    }, 3000)
  }
}

async function handleRotate() {
  const success = await store.rotateSeed()
  if (success) {
    showRotateConfirm.value = false
    store.fetchHistory(1)
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
  copyFeedback.value = text
  setTimeout(() => {
    copyFeedback.value = null
  }, 2000)
}

function truncate(str: string, len: number): string {
  if (str.length <= len) return str
  return str.slice(0, len) + '...'
}

function formatDate(iso: string | null): string {
  if (!iso) return '-'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

onMounted(() => {
  store.fetchActiveSeeds()
  store.fetchHistory(1)
})
</script>

<style scoped>
.provably-fair {
  padding: var(--space-6, 24px);
  max-width: 900px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
}

.page-desc {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 28px;
  line-height: 1.5;
}

/* Sections */
.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary, #fff);
}

.section-title svg {
  color: var(--accent-primary, #00e701);
}

.section-desc {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 14px;
  line-height: 1.5;
}

/* Seeds Card */
.seeds-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.seed-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.seed-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.seed-value-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seed-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  color: var(--text-primary, #fff);
  background: rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  word-break: break-all;
  flex: 1;
}

.seed-value.hash {
  font-size: 11px;
  color: var(--accent-primary, #00e701);
}

.seed-value.nonce {
  width: fit-content;
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-secondary, #1475e1);
}

.seed-hint {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  line-height: 1.4;
}

/* Copy Button */
.btn-copy {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  flex-shrink: 0;
}

.btn-copy:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-copy-sm {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  cursor: pointer;
  transition: color var(--transition-fast, 150ms ease);
  flex-shrink: 0;
}

.btn-copy-sm:hover {
  color: var(--accent-primary, #00e701);
}

/* Client Seed Form */
.client-seed-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.text-input {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 11px 70px 11px 14px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  transition: border-color var(--transition-fast, 150ms ease);
}

.text-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.text-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-family-primary, 'Inter', sans-serif);
}

.char-count {
  position: absolute;
  right: 12px;
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.char-count.warn {
  color: var(--color-warning, #ffa726);
}

.validation-msg {
  font-size: 12px;
  color: var(--color-warning, #ffa726);
}

.validation-msg.error {
  color: var(--color-danger, #ed4245);
}

.validation-msg.success {
  color: var(--color-success, #00e701);
}

/* Buttons */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 22px;
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
  width: fit-content;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-secondary-hover, #1260bf);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-rotate {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: var(--bg-tertiary, #213743);
  color: var(--color-warning, #ffa726);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 168, 38, 0.3);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-rotate:hover {
  background: rgba(255, 168, 38, 0.1);
  border-color: rgba(255, 168, 38, 0.5);
  transform: translateY(-1px);
}

.btn-cta {
  padding: 11px 22px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-cta:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

.btn-secondary {
  padding: 10px 20px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-danger, #d63031);
  box-shadow: var(--shadow-glow-red, 0 0 20px rgba(237, 66, 69, 0.3));
  transform: translateY(-1px);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-text {
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  transition: color var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-text:hover {
  color: var(--text-primary, #fff);
}

/* Confirmation Modal */
.confirm-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal, rgba(15, 25, 35, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 400);
  animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.confirm-modal {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
}

.modal-desc {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Rotation Result */
.rotation-result {
  margin-top: 16px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
}

.rotation-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-success, #00e701);
  margin-bottom: 16px;
}

.rotation-compare {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: start;
}

.rotation-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rotation-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
}

.rotation-col.old .rotation-label {
  color: var(--color-warning, #ffa726);
}

.rotation-col.new .rotation-label {
  color: var(--color-success, #00e701);
}

.rotation-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rotation-field-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
}

.rotation-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 32px;
  color: var(--text-tertiary, #7f8fa4);
}

.rotation-verify-hint {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 14px;
  font-style: italic;
}

/* History Table */
.history-table-wrap {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.history-header {
  display: grid;
  grid-template-columns: 1.2fr 1.5fr 1.5fr 1fr 0.5fr;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.history-row {
  display: grid;
  grid-template-columns: 1.2fr 1.5fr 1.5fr 1fr 0.5fr;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  align-items: center;
  font-size: 13px;
  transition: background var(--transition-fast, 150ms ease);
}

.history-row:last-child {
  border-bottom: none;
}

.history-row:hover {
  background: rgba(0, 231, 1, 0.02);
}

.history-date {
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
}

.history-seed,
.history-hash,
.history-client {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mono-sm {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 11px;
  color: var(--text-primary, #fff);
}

.history-nonce {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-secondary, #1475e1);
}

/* History Pagination */
.history-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0 8px;
}

.btn-page {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-page:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-page:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
}

/* Verify Link */
.verify-link-section {
  padding-top: 8px;
}

.btn-verify-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  background: linear-gradient(135deg, rgba(0, 231, 1, 0.08) 0%, rgba(0, 231, 1, 0.03) 100%);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: var(--radius-lg, 12px);
  color: var(--accent-primary, #00e701);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-verify-link:hover {
  background: linear-gradient(135deg, rgba(0, 231, 1, 0.15) 0%, rgba(0, 231, 1, 0.06) 100%);
  border-color: rgba(0, 231, 1, 0.4);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

/* Error & Empty States */
.pf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  gap: 12px;
}

.pf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  gap: 6px;
}

.pf-empty-sub {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Skeleton Loading */
.seed-row-skeleton,
.history-row-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
}

.skel-label {
  height: 12px;
  width: 30%;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-value {
  height: 16px;
  width: 80%;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Spinner */
.spinner {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .provably-fair {
    padding: var(--space-4, 16px);
  }

  .rotation-compare {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .rotation-divider {
    padding-top: 0;
    transform: rotate(90deg);
  }

  .history-header,
  .history-row {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .history-header span:nth-child(3),
  .history-header span:nth-child(4),
  .history-row .history-hash,
  .history-row .history-client {
    display: none;
  }

  .seed-value.hash {
    font-size: 10px;
  }
}
</style>
